// The hero scene: one Pulse document built with the Builder SDK in the page. Everything in it is
// drawn by the runtime from data: morphing vector symbols in an orbiting vortex, stroked rings
// with dashes and trims, a draw-on pulse line, particles on parabolas, thrown sparks, filters
// (glow, drop shadow, bevel), two tentacles on bone rigs (one forward-kinematic, one reaching by
// IK), 3D-rotated cards under a keyed camera, kinetic type, audio widgets and a post chain.
//
// `stress` multiplies the object counts; `fx` switches the costly effect groups so a visitor
// can see what each costs. The layout adapts to the stage's aspect (landscape or portrait).
import { BPM, DURATION } from '../lib/soundtrack.js';

export const PALETTE = {
  bg: '#07040f',
  cyan: '#22e8ff',
  magenta: '#ff2bd1',
  violet: '#7b5cff',
  volt: '#c8ff2e',
  orange: '#ff7a1a',
  white: '#ffffff',
};
const C = PALETTE;
const HUES = [C.cyan, C.magenta, C.violet, C.volt, C.orange];

export const BASE = { shapes: 1600, morphs: 120, particles: 2600, sparks: 48 };

export const DEFAULT_FX = { filters: true, bloom: true, trails: false, blur: true };

const r3 = (v) => Math.round(v * 1000) / 1000;
const r1 = (v) => Math.round(v * 10) / 10;

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Sixteen-vertex outlines centred on (c, c), so any two morph into each other.
const N = 16;
function star(c, R, r) {
  return Array.from({ length: N }, (_, i) => { const a = -Math.PI / 2 + i * Math.PI * 2 / N, k = i % 2 ? r : R; return { x: r1(c + Math.cos(a) * k), y: r1(c + Math.sin(a) * k) }; });
}
function roundish(c, R) { return star(c, R, R); }
function square(c, R) {
  // Four vertices per side, walking the square clockwise from its top-left corner.
  const pts = [];
  const s = R * 0.86;
  const side = (x0, y0, x1, y1) => { for (let i = 0; i < 4; i++) pts.push({ x: r1(c + x0 + (x1 - x0) * i / 4), y: r1(c + y0 + (y1 - y0) * i / 4) }); };
  side(-s, -s, s, -s); side(s, -s, s, s); side(s, s, -s, s); side(-s, s, -s, -s);
  // Rotate the list so vertex 0 sits near the top like the others.
  return pts.slice(2).concat(pts.slice(0, 2));
}
function diamond(c, R) {
  return Array.from({ length: N }, (_, i) => {
    const a = -Math.PI / 2 + i * Math.PI * 2 / N;
    const x = Math.cos(a), y = Math.sin(a), k = 1 / (Math.abs(x) + Math.abs(y));
    return { x: r1(c + x * k * R), y: r1(c + y * k * R * 1.25) };
  });
}

/** Keys that cycle through `values` every `period` seconds for `dur` seconds, ending where they began. */
function cycle(values, period, dur, ease = 'in_out_cubic') {
  const out = [];
  const step = period / values.length;
  for (let t = 0, i = 0; t <= dur + 1e-9; t += step, i++) out.push({ t: r3(t), v: values[i % values.length], ease });
  return { keys: out };
}

function morphSymbols(d) {
  const S = 60, c = S / 2;
  const shapes = [star(c, 28, 12), roundish(c, 24), square(c, 24), diamond(c, 26), star(c, 28, 20)];
  const variants = [
    { id: 'm0', order: [0, 1, 3, 1], fill: C.cyan },
    { id: 'm1', order: [2, 0, 4, 3], fill: C.magenta },
    { id: 'm2', order: [3, 4, 1, 0], fill: C.violet },
    { id: 'm3', order: [1, 2, 0, 4], fill: C.volt },
    { id: 'm4', order: [4, 3, 2, 1], fill: { linear: { from: { x: 0, y: 0 }, to: { x: S, y: S }, stops: [{ at: 0, color: C.magenta }, { at: 1, color: C.cyan }] } } },
    { id: 'm5', order: [0, 2, 1, 3], fill: null, stroke: C.cyan },
  ];
  for (const v of variants) {
    d.defineSymbol(v.id, { duration: 4, width: S, height: S, loop: true }, (tl) => {
      tl.shape([{ closed: true, vertices: cycle(v.order.map((k) => shapes[k]), 4, 4) }], v.stroke
        ? { fill: null, stroke: v.stroke, strokeWidth: 3, join: 'round' }
        : { fill: v.fill });
    });
  }
  return { morph: variants.map((v) => v.id) };
}

function cardSymbols(d, words) {
  const w = 190, h = 118;
  words.forEach((word, i) => {
    const hue = HUES[i % HUES.length];
    d.defineSymbol('card' + i, { duration: 4, width: w, height: h }, (tl) => {
      tl.rect(0, 0, w, h, { radius: 18, fill: { linear: { from: { x: 0, y: 0 }, to: { x: w, y: h }, stops: [{ at: 0, color: '#1a1030' }, { at: 1, color: '#0b0718' }] } }, stroke: hue, strokeWidth: 3 });
      tl.rect(16, 18, 38, 6, { radius: 3, fill: hue });
      tl.text(word, { size: 26, weight: 800, fill: '#ffffff', tracking: 2, x: w / 2, y: h / 2 + 8 });
      tl.circle(w - 24, 24, 6, { fill: null, stroke: hue, strokeWidth: 2, trim: { start: 0, end: 0.7, offset: { keys: [{ t: 0, v: 0 }, { t: 4, v: 1 }] } } });
    });
  });
  return { w, h };
}

/**
 * Build the hero document.
 * o: {width, height, stress, fx, audioUrl, seed}
 * Returns {doc, counts} where counts lists what the document holds.
 */
export function heroDoc(Pulse, o = {}) {
  const W = o.width || 1600;
  const H = o.height || 900;
  const m = o.stress ?? 1;
  const fx = { ...DEFAULT_FX, ...(o.fx || {}) };
  const D = DURATION;
  const S = Math.min(W, H), portrait = H > W;
  // The focus sits right of centre on wide stages (the page's headline is on the left) and
  // above centre on tall ones (the headline is at the bottom).
  const cx = W / H > 1.3 ? W * 0.57 : W / 2, cy = portrait ? H * 0.4 : H / 2;
  const rand = rng(o.seed ?? 7);
  const { keys } = Pulse;
  const counts = { shapes: 0, emitters: 0, particleMax: 0, sparks: 0, cards: 0, bones: 0, text: 0, widgets: 0 };

  const d = Pulse.doc({ width: W, height: H, duration: D, fps: 30, background: C.bg, title: 'Pulse hero' })
    .beats(BPM, { until: D })
;
  d.json.root.loop = true;

  // Camera: a slow breathing push-in with a little roll, looping.
  d.camera({
    zoom: keys([0, 1, 'in_out_sine'], [D / 2, 1.08, 'in_out_sine'], [D, 1]),
    rotation: keys([0, -1.5, 'in_out_sine'], [D / 2, 1.5, 'in_out_sine'], [D, -1.5]),
  });

  // ---- background: radial gradient (fixed, outside the camera) with slow hue drift
  const bg = d.layer('bg').fixed();
  bg.solid({ radial: { center: { x: cx, y: cy }, radius: Math.max(W, H) * 0.75, stops: [
    { at: 0, color: keys([0, '#241049'], [D / 2, '#0d2a4a'], [D, '#241049']) },
    { at: 0.55, color: '#0b0620' },
    { at: 1, color: C.bg },
  ] } });
  counts.shapes += 1;

  // ---- blurred colour blobs (a full-layer blur pass when fx.blur)
  const blobs = d.layer('blobs');
  if (fx.blur) blobs.post('blur', { radius: 26 });
  for (let i = 0; i < 9; i++) {
    const a = i / 9 * Math.PI * 2, rr = S * (0.32 + (i % 3) * 0.08);
    blobs.circle(0, 0, S * (0.07 + (i % 4) * 0.025), { fill: HUES[i % HUES.length] + '55', x: cx + Math.cos(a) * rr * (portrait ? 0.8 : 1.4), y: cy + Math.sin(a) * rr * 0.7 })
      .drift(S * 0.06, { frequency: 0.12 + i * 0.01, seed: i + 3 });
    counts.shapes++;
  }

  // ---- stroked rings: dashes marching, trimmed arcs sweeping (vector strokes)
  const rings = d.layer('rings');
  for (let i = 0; i < 7; i++) {
    const R = S * (0.17 + i * 0.045);
    const hue = HUES[i % HUES.length];
    if (i % 2 === 0) {
      rings.circle(cx, cy, R, { fill: null, stroke: hue + 'aa', strokeWidth: 2 + (i % 3), cap: 'round' })
        .marchingAnts([6 + i * 3, 10 + i * 2], (i % 4 ? 1 : -1) * (30 + i * 10));
    } else {
      rings.circle(cx, cy, R, { fill: null, stroke: hue, strokeWidth: 4, cap: 'round', trim: { start: 0, end: 0.18 + i * 0.03, offset: keys([0, i * 0.13], [D, i * 0.13 + (i % 4 === 1 ? 2 : -2)]) } });
    }
    counts.shapes++;
  }

  // ---- the vortex: morphing vector symbols orbiting the centre
  const ids = morphSymbols(d);
  // Static outlines drawn straight on the layer: their geometry stays on the GPU and consecutive
  // plain-colour shapes join the renderer's batches. Morphing ones are re-flattened every frame.
  const STILL = [star(0, 28, 12), diamond(0, 26), star(0, 28, 20), square(0, 24), roundish(0, 24), star(0, 22, 9)];
  // Alpha falls off with radius; fill alpha is part of the plain colour, so it does not break batches.
  const STILL_HUE = [C.cyan, C.magenta, C.violet, C.cyan, '#ffffff', C.volt];
  const alpha = (u) => ['ff', 'cc', '99', '66'][Math.min(3, Math.floor(u * 4))];
  // Two layers so the batched statics are never interrupted by a morphing symbol.
  const vortex = d.layer('vortex');
  const morphs = d.layer('morphs');
  const ex = portrait ? 0.7 : 1.45, ey = portrait ? 1.1 : 0.58;
  const ctr = { x: cx, y: cy };
  // A three-armed spiral galaxy: each shape orbits the centre on a tilted ellipse, starting on its arm.
  const nV = Math.round(BASE.shapes * m);
  for (let i = 0; i < nV; i++) {
    const u = rand();
    const rr = S * (0.14 + 0.66 * u);
    const arm = i % 3;
    const from = r1(arm * 120 + u * 320 + (rand() - 0.5) * 38 * (1 + u));
    const turns = u < 0.45 ? 2 : 1;
    const k = i % STILL.length;
    const scale = r3((0.1 + 0.28 * rand()) * (1.25 - 0.5 * u));
    vortex.shape([{ closed: true, vertices: STILL[k] }], { fill: STILL_HUE[k] + alpha(u), scale, rotation: 0 })
      .orbit(ctr, { x: r1(rr * ex), y: r1(rr * ey) }, { turns, from, duration: D })
      .keys('rotation', [[0, 0], [D, (i % 2 ? 360 : -360) * (1 + (i % 3))]]);
    counts.shapes++;
  }
  // Morphing symbols ride the outer ring, larger, each on its own clock.
  const nM = Math.round(BASE.morphs * m);
  for (let i = 0; i < nM; i++) {
    const u = 0.55 + 0.45 * rand();
    const rr = S * (0.14 + 0.66 * u);
    morphs.symbol(ids.morph[i % ids.morph.length], { speed: r3(0.6 + rand() * 0.9), scale: r3(0.45 + rand() * 0.5), rotation: 0 })
      .orbit(ctr, { x: r1(rr * ex), y: r1(rr * ey) }, { turns: 1, from: r1(rand() * 360), duration: D })
      .keys('rotation', [[0, 0], [D, (i % 2 ? 360 : -360)]]);
    counts.shapes++;
    counts.morphs = (counts.morphs || 0) + 1;
  }

  // ---- the pulse line: an ECG-like bezier across the frame, drawn on and off every bar
  const beat = 60 / BPM, bar = beat * 4;
  const px0 = W * 0.04, px1 = W * 0.96, py = portrait ? H * 0.8 : H * 0.78;
  const pulsePts = [];
  const segs = 24;
  for (let i = 0; i <= segs; i++) {
    const x = px0 + (px1 - px0) * i / segs;
    const spike = i % 6 === 3 ? -S * 0.12 : i % 6 === 4 ? S * 0.07 : 0;
    pulsePts.push({ x: r1(x), y: r1(py + spike) });
  }
  const tStart = [], tEnd = [];
  for (let b = 0; b * bar < D - 1e-9; b++) {
    const t0 = b * bar;
    tEnd.push({ t: r3(t0), v: 0, ease: 'out_cubic' }, { t: r3(t0 + bar * 0.45), v: 1 });
    tStart.push({ t: r3(t0 + bar * 0.35), v: 0, ease: 'in_cubic' }, { t: r3(t0 + bar * 0.9), v: 1 });
  }
  tEnd.push({ t: D, v: 0 }); tStart.push({ t: D, v: 0 });
  const line = d.layer('pulse-line').shape([{ closed: false, vertices: pulsePts }], { fill: null, stroke: C.volt, strokeWidth: 4, cap: 'round', join: 'round', trim: { start: { keys: tStart }, end: { keys: tEnd } } });
  if (fx.filters) line.effect('glow', { radius: 12, intensity: 1.3, color: C.volt });
  counts.shapes++;

  // ---- particles on parabolas: two fountains under gravity, a snowfall of dots, beat bursts
  const pl = d.layer('particles').blend('add');
  const pRate = BASE.particles * m;
  // An emitter holds at most 20 000 particles and 10 000 a second (the format's limits), so a
  // big one is split into several with different seeds.
  let emitters = 0, particleMax = 0;
  const emit = (em) => {
    const parts = Math.max(1, Math.ceil(Math.max((em.max || 0) / 20000, (em.rate || 0) / 10000, (em.burst?.[0]?.count || 0) / 20000)));
    for (let k = 0; k < parts; k++) {
      const e = { ...em, seed: em.seed * 101 + k };
      if (e.rate) e.rate = r1(e.rate / parts);
      if (e.max) e.max = Math.ceil(e.max / parts);
      if (e.burst) e.burst = e.burst.map((b) => ({ t: b.t, count: Math.ceil(b.count / parts) }));
      pl.particles(e, { blend: 'add' });
      emitters++; particleMax += e.max || 0;
    }
  };
  const fountain = (x, angle, seed, colors) => emit({
    seed, rate: r1(pRate / 5), lifetime: [2, 3.2], max: Math.round(pRate / 5 * 3.2),
    shape: { circle: { x, y: H + 6, r: S * 0.02 } },
    velocity: { angle, speed: [H * 0.75, H * 1.15] }, gravity: { x: 0, y: H * 0.55 },
    size: [2.5, 7], opacity_over_life: [0, 1, 1, 0], color_over_life: colors, blend: 'add',
  });
  fountain(W * 0.18, [262, 292], 11, [C.cyan, C.violet]);
  fountain(W * 0.82, [248, 278], 12, [C.magenta, C.orange]);
  emit({
    seed: 13, rate: r1(pRate / 4), lifetime: [4, 7], max: Math.round(pRate / 4 * 7),
    shape: { rect: { x: 0, y: -10, w: W, h: 4 } }, velocity: { angle: [80, 100], speed: [30, 90] },
    size: [1.5, 3.5], opacity_over_life: [0, 0.9, 0], color_over_life: ['#ffffff', C.cyan], blend: 'add',
  });
  const bursts = [];
  for (let b = 0; b * bar < D - 1e-9; b++) bursts.push({ t: r3(b * bar), count: Math.round(pRate / 10) });
  emit({
    seed: 14, burst: bursts, lifetime: [1.2, 2.2], max: Math.round(pRate / 10 * 2),
    shape: { circle: { x: cx, y: cy, r: 8 } }, velocity: { angle: [0, 360], speed: [S * 0.3, S * 0.75] },
    gravity: { x: 0, y: S * 0.5 }, drag: 0.4, size: [2, 6], opacity_over_life: [1, 0], color_over_life: [C.volt, C.magenta], blend: 'add',
  });
  counts.emitters = emitters;
  counts.particleMax = particleMax;

  // Thrown sparks: vector diamonds on parabola motion paths, launched on every beat.
  const sparks = d.layer('sparks');
  const nS = Math.round(BASE.sparks * m);
  const beats = Math.round(D / beat);
  for (let i = 0; i < nS; i++) {
    const b = i % beats, t0 = r3(b * beat), a = (-150 + rand() * 120) * Math.PI / 180, sp = S * (0.7 + rand() * 0.5);
    sparks.polygon([[0, -9], [5, 0], [0, 9], [-5, 0]], { fill: HUES[i % HUES.length], start: t0, end: r3(Math.min(D, t0 + 1.6)) })
      .throw({ x: cx, y: cy }, { x: r1(Math.cos(a) * sp), y: r1(Math.sin(a) * sp) }, { gravity: { x: 0, y: S * 1.2 }, orient: true })
      .keys('opacity', [[t0, 1], [r3(Math.min(D, t0 + 1.6)), 0]]);
    counts.shapes++;
  }
  counts.sparks = nS;

  // ---- bones: two tentacles skinned to five bones each (automatic weights)
  // dir 1 grows right from (x0, y0), -1 grows left. Bone angles are world rest angles.
  const tentacle = (id, x0, y0, len, dir, hueA, hueB) => {
    const n = 24, r = S * 0.04, top = [], bot = [];
    for (let i = 0; i <= n; i++) { const x = x0 + dir * len * i / n, w = r * (1 - 0.88 * i / n); top.push(r1(x), r1(y0 - w)); bot.unshift(r1(x), r1(y0 + w)); }
    const t = d.layer(id).shape([{ closed: true, points: [...top, ...bot], smooth: 0.16 }], {
      id, fill: { linear: { from: { x: x0, y: 0 }, to: { x: x0 + dir * len, y: 0 }, stops: [{ at: 0, color: hueA }, { at: 0.7, color: hueB }, { at: 1, color: hueB + '00' }] } }, stroke: '#ffffff40', strokeWidth: 1.5,
    });
    if (fx.filters) t.effect('glow', { radius: 16, intensity: 0.9, color: hueB });
    const arm = d.armature(id + '-rig');
    let parent = null;
    for (let i = 0; i < 5; i++) {
      arm.bone(id + i, { parent, x: parent ? undefined : x0, y: parent ? undefined : y0, length: len / 5, angle: dir > 0 ? 0 : 180, ...(i === 4 && dir < 0 ? { ik: { chain: 5 } } : {}) });
      parent = id + i;
    }
    arm.skin(id, { influences: 2, smooth: 2 });
    counts.bones += 5;
    counts.shapes++;
    return arm;
  };
  const tl = S * 0.6, ty = H * 1.02;
  // Left: forward kinematics, a travelling sway (each bone lags the one before).
  const left = tentacle('tentL', portrait ? -S * 0.05 : W * 0.44, portrait ? ty : -S * 0.04, tl, 1, C.violet, C.cyan);
  for (let i = 0; i < 5; i++) {
    const k = [];
    for (let t = 0, j = 0; t <= D + 1e-9; t += beat * 2, j++) k.push([r3(t), (i === 0 ? (portrait ? -55 : 40) : 0) + (j % 2 ? -1 : 1) * (14 + i * 7) * (i % 2 ? -1 : 1), 'in_out_sine']);
    left.rotate('tentL' + i, keys(...k));
  }
  // Right: an IK chain whose tip reaches for a target circling above it.
  const rx0 = portrait ? W + S * 0.05 : W * 0.98;
  const right = tentacle('tentR', rx0, ty, tl, -1, C.orange, C.magenta);
  const tk = [];
  for (let i = 0; i <= 16; i++) {
    const a = i / 16 * Math.PI * 2;
    tk.push([r3(i / 16 * D), { x: r1(rx0 - tl * 0.55 + Math.cos(a) * tl * 0.3), y: r1(ty - tl * 0.55 + Math.sin(a) * tl * 0.25) }, 'in_out_sine']);
  }
  right.target('tentR4', keys(...tk));

  // ---- 3D cards on a carousel around the title, rotated in depth under the camera
  const words = ['VECTOR', 'PARTICLES', 'BONES', '3D + CAMERA', 'FILTERS', 'AUDIO', 'SCRIPT', 'EXPORT'];
  const card = cardSymbols(d, words);
  // The cards live in one symbol with its own perspective, so the 3D projection applies to them
  // and not to the thousands of flat shapes (flat 2D matrices are what the shape batches need).
  const carousel = d.defineSymbol('carousel', { duration: D, width: W, height: H });
  carousel.perspective({ fov: 55 });
  const Rx = portrait ? W * 0.36 : S * 0.62, Rz = S * 0.9;
  const cardY = portrait ? cy - S * 0.42 : cy - S * 0.33;
  words.forEach((_, i) => {
    const kx = [], kz = [], kr = [];
    for (let s2 = 0; s2 <= 32; s2++) {
      const t = r3(s2 / 32 * D), th = (i / words.length + s2 / 32) * Math.PI * 2;
      kx.push([t, r1(cx + Math.sin(th) * Rx)]);
      kz.push([t, r1(Rz * 0.5 * (1 - Math.cos(th)))]);
      kr.push([t, r1(-Math.sin(th) * 55)]);
    }
    const inst = carousel.symbol('card' + i, { x: cx, y: r1(cardY + (i % 2) * S * 0.04), scale: portrait ? 0.9 : 1 })
      .keys('x', kx).keys('z', kz).keys('rotation_y', kr).keys('rotation_x', [[0, 8, 'in_out_sine'], [D / 2, -8, 'in_out_sine'], [D, 8]]);
    if (fx.filters) inst.effect('drop_shadow', { distance: 12, angle: 90, blur: 14, alpha: 0.6 }).effect('bevel', { distance: 3, blur: 4, angle: 45, strength: 0.8 });
    counts.shapes += 4;
  });
  d.layer('cards').symbol('carousel', { x: cx, y: cy });
  counts.cards = words.length;
  void card;

  // ---- audio widgets (synthetic signals until the soundtrack plays)
  const wl = d.layer('widgets');
  const rs = S * (portrait ? 0.62 : 0.5);
  wl.widget('radial_spectrum', { bins: 96, inner: 0.62, outer: 1, mirror: true, glow: 0.8, paint: { linear: { from: { x: 0, y: 0 }, to: { x: rs, y: rs }, stops: [{ at: 0, color: C.cyan }, { at: 1, color: C.magenta }] } }, rotation: { keys: [{ t: 0, v: 0 }], expr: 'T*14' } }, { size: { w: r1(rs), h: r1(rs) }, x: cx, y: cy });
  wl.widget('rings', { on: 'beat', life: 1.1, thickness: 3, paint: C.volt }, { size: { w: r1(rs * 1.3), h: r1(rs * 1.3) }, x: cx, y: cy, opacity: 0.7 });
  wl.widget('spectrum', { bins: 72, mirror: 'horizontal', peak_hold: 1, gap: 0.3, paint: { linear: { from: { x: 0, y: 0 }, to: { x: 0, y: 120 }, stops: [{ at: 0, color: C.magenta }, { at: 1, color: C.violet + '00' }] } } }, { size: { w: W, h: r1(H * 0.14) }, left: 0, top: r1(H * 0.86), opacity: 0.75 });
  wl.widget('oscilloscope', { mode: 'line', thickness: 2, persistence: 0.6, glow: 0.7, paint: C.cyan }, { size: { w: r1(W * 0.5), h: r1(S * 0.1) }, x: cx, y: r1(cy + S * (portrait ? 0.5 : 0.3)), opacity: 0.8 });
  counts.widgets = 4;

  // ---- kinetic type: one word per two bars, letters in and out, pulsing on the beat
  const kt = d.layer('type');
  const kinetic = ['PULSE', 'VECTOR', 'MOTION', 'LIVE'];
  const size = Math.round(S * (portrait ? 0.15 : 0.14));
  kinetic.forEach((word, i) => {
    const t0 = i * bar * 2, t1 = t0 + bar * 2;
    const tx = kt.text(word, { size, weight: 900, tracking: Math.round(size * 0.06), fill: { linear: { from: { x: 0, y: -size / 2 }, to: { x: 0, y: size / 2 }, stops: [{ at: 0, color: '#ffffff' }, { at: 1, color: HUES[i % HUES.length] }] } }, x: cx, y: cy, start: r3(t0), end: r3(t1) })
      .reveal('char', { stagger: 0.05, duration: 0.5, ease: 'out_back', from: { opacity: 0, y: size * 0.4, scale: 0.4, rotation: -20 }, out: { duration: 0.35, to: { opacity: 0, y: -size * 0.3, scale: 1.3 } } })
      .onBeat(0.05);
    if (fx.filters) tx.effect('glow', { radius: 18, intensity: 1.1, color: HUES[i % HUES.length] });
    counts.text++;
  });

  // ---- post chain
  if (fx.bloom) d.post('bloom', { radius: 20, threshold: 0.66, intensity: Pulse.bind('bass', { value: 0.45, gain: 0.6 }) });
  if (fx.trails) d.post('feedback', { amount: 0.5, zoom: 1.006 });
  d.post('chromatic', { offset: Pulse.bind('onset', { value: 1, gain: 2.5 }) });
  d.post('vignette', { amount: 0.55 });
  d.post('film_grain', { amount: 0.04 });

  if (o.audioUrl) {
    d.audioAsset('loop', o.audioUrl, { duration: D });
    d.track('mix', 'loop', { kind: 'mix' });
    d.master({ fx: [{ type: 'limiter', ceiling_db: -1 }] });
  }

  return { doc: d.build(), counts };
}
